import React, { useEffect, useState } from 'react';
import {
  Card,
  Form,
  Input,
  DatePicker,
  Select,
  Button,
  Space,
  message,
  Typography,
  Row,
  Col,
  Checkbox,
  Upload,
  Table,
  Tag,
  Tooltip,
  Tabs,
  Drawer,
  Progress,
} from 'antd';
import {
  ArrowLeftOutlined,
  CheckOutlined,
  PlusOutlined,
  DeleteOutlined,
  UploadOutlined,
  EyeOutlined,
  DownloadOutlined,
} from '@ant-design/icons';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { hasPermission } from '../utils/permissions';
import {
  letterService,
  letterItemService,
  letterCommentService,
  letterReviewService,
  Letter,
  CreateLetterData,
  LetterSubTask,
  LetterItem,
  LetterComment,
  LetterReview,
} from '../services/letters';
import { customerService } from '../services/customers';
import { businessService } from '../services/businesses';
import { subjectService, Subject } from '../services/subjects';
import { staffService, Staff } from '../services/staffs';
import { taskTemplateService, TaskTemplate } from '../services/taskTemplates';
import { SubTaskTemplate } from '../services/tasks';
import api from '../services/api';
import dayjs from 'dayjs';

const { Title, Text } = Typography;
const { TextArea } = Input;
const { TabPane } = Tabs;

export const LetterFormPage: React.FC = () => {
  const { id } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const isEdit = Boolean(id);
  const [form] = Form.useForm();
  const { user } = useAuth();
  const canUpdate = hasPermission(user, 'letters.update');
  const canDelete = hasPermission(user, 'letters.delete');
  const [submitting, setSubmitting] = useState(false);
  const [loadingLetter, setLoadingLetter] = useState(false);
  const [activeTab, setActiveTab] = useState<string>(searchParams.get('tab') || 'overview');
  const [customers, setCustomers] = useState<any[]>([]);
  const [businesses, setBusinesses] = useState<any[]>([]);
  const [customerType, setCustomerType] = useState<'personal' | 'business'>('personal');
  const [selectedCustomer, setSelectedCustomer] = useState<any>(null);
  const [selectedBusiness, setSelectedBusiness] = useState<any>(null);
  const [subjects, setSubjects] = useState<Subject[]>([]);
  const [staff, setStaff] = useState<Staff[]>([]);
  const [teams, setTeams] = useState<any[]>([]);
  const [subTaskTemplates, setSubTaskTemplates] = useState<SubTaskTemplate[]>([]);
  const [taskTemplates, setTaskTemplates] = useState<TaskTemplate[]>([]);
  const [letterSubTasks, setLetterSubTasks] = useState<LetterSubTask[]>([]);
  const [letterItems, setLetterItems] = useState<Array<LetterItem | { id: string; letter_id: number; attachment: null; enabled: boolean; created_at: string; creator?: any; file: File }>>([]);
  const [comments, setComments] = useState<LetterComment[]>([]);
  const [reviews, setReviews] = useState<LetterReview[]>([]);
  const [commentText, setCommentText] = useState('');
  const [reviewText, setReviewText] = useState('');
  const [uploadQueue, setUploadQueue] = useState<Array<{ file: File; id: string }>>([]);
  const [uploadStatus, setUploadStatus] = useState<Record<string, 'pending' | 'uploading' | 'success' | 'error'>>({});
  const [uploadProgress, setUploadProgress] = useState<Record<string, number>>({});
  const [subjectDrawerVisible, setSubjectDrawerVisible] = useState(false);
  const [newSubjectName, setNewSubjectName] = useState('');
  const [letterName, setLetterName] = useState<string>('');
  const [isEditing, setIsEditing] = useState(!isEdit); // For new letters, default to editing mode
  const navigate = useNavigate();

  useEffect(() => {
    fetchInitialData();
    if (isEdit && id) {
      loadLetter(parseInt(id, 10));
    } else {
      // Set default status for new letters
      form.setFieldsValue({ status: 'Plan', is_sent: false, sender_id: user?.id || undefined });
    }
  }, [isEdit, id, user?.id]);

  // Sync activeTab with URL parameter
  useEffect(() => {
    const tabFromUrl = searchParams.get('tab');
    if (tabFromUrl) {
      setActiveTab(tabFromUrl);
    }
  }, [searchParams]);

  // Handle tab change and update URL
  const handleTabChange = (key: string) => {
    setActiveTab(key);
    setSearchParams({ tab: key });
  };

  const fetchInitialData = async () => {
    try {
      const [customersResponse, businessesResponse, subjectsResponse, staffResponse, teamsResponse, subTasksResponse, taskTemplatesResponse] = await Promise.all([
        customerService.getAll(),
        businessService.getAll().catch(() => ({ data: [] })),
        subjectService.getAll(),
        staffService.getAll(),
        api.get('/teams'),
        api.get('/sub-tasks').catch(() => ({ data: { data: [] } })),
        taskTemplateService.getAll().catch(() => ({ data: [] })),
      ]);
      setCustomers(customersResponse.data || customersResponse || []);
      setBusinesses(businessesResponse.data || []);
      setSubjects(subjectsResponse || []);
      setStaff(Array.isArray(staffResponse) ? staffResponse : []);
      setTeams(teamsResponse.data?.data || teamsResponse.data || []);
      setSubTaskTemplates(subTasksResponse.data?.data || subTasksResponse.data || []);
      setTaskTemplates(taskTemplatesResponse.data || taskTemplatesResponse || []);
    } catch {
      message.error('Failed to load initial data');
    }
  };

  const loadLetter = async (letterId: number) => {
    setLoadingLetter(true);
    try {
      const letter = await letterService.getById(letterId);
      const resolvedType = (letter as any).type ?? ((letter as any).business_id ? 'business' : 'personal');
      const resolvedReferenceId = (letter as any).reference_id ?? ((letter as any).business_id ?? letter.customer_id);
      // Map old status values to new ones
      const mapStatus = (status: string | undefined): 'Plan' | 'Unread' | 'Read' => {
        if (!status) return 'Plan';
        if (status === 'draft') return 'Plan';
        if (status === 'sent') return 'Unread';
        if (['Plan', 'Unread', 'Read'].includes(status)) return status as 'Plan' | 'Unread' | 'Read';
        return 'Plan';
      };
      const mappedStatus = mapStatus(letter.status);
      form.setFieldsValue({
        customer_id: letter.customer_id,
        sender_id: letter.sender_id,
        subject_id: letter.subject_id,
        create_date: letter.create_date ? dayjs(letter.create_date) : null,
        due_date: letter.due_date ? dayjs(letter.due_date) : null,
        team_id: letter.team_id,
        status: mappedStatus,
        is_sent: (letter as any).is_sent !== undefined ? (letter as any).is_sent : false,
        type: resolvedType,
        reference_id: resolvedReferenceId,
      });
      
      setCustomerType(resolvedType);
      // Handle business_id
      if (resolvedType === 'business' && resolvedReferenceId) {
        form.setFieldsValue({ business_id: resolvedReferenceId, reference_id: resolvedReferenceId });
        let business = businesses.find(b => b.id === resolvedReferenceId)
          || businesses.find(b => b.id === (letter as any).business_id);
        if (!business) {
          try {
            business = await businessService.getById(resolvedReferenceId);
          } catch {
            business = undefined;
          }
        }
        if (business) {
          setSelectedBusiness(business);
          if (business.customer_id) {
            try {
              const customer = await customerService.getById(business.customer_id);
              setSelectedCustomer(customer);
              form.setFieldsValue({ customer_id: business.customer_id });
            } catch {
              // Ignore error
            }
          }
        }
      } else if (resolvedType === 'personal' && resolvedReferenceId) {
        form.setFieldsValue({ customer_id: resolvedReferenceId, reference_id: resolvedReferenceId });
        try {
          const customer = await customerService.getById(resolvedReferenceId);
          setSelectedCustomer(customer);
        } catch {
          // Ignore error
        }
      }
      
      setLetterSubTasks(letter.subTasks || []);
      setLetterItems(letter.items || []);
      setComments(letter.comments || []);
      setReviews(letter.reviews || []);
      
      // Set letter name for header display
      const formatLetterNumber = (id: number) => `#${id.toString().padStart(7, '0')}`;
      if (letter.customer_id && letter.subject_id) {
        const subject = subjects.find(s => s.id === letter.subject_id);
        const isBusiness = Boolean((letter as any).business_id);
        setLetterName(`${formatLetterNumber(letterId)} - ${isBusiness ? 'Business' : 'Personal'} - ${subject?.name || 'Subject'}`);
      } else {
        setLetterName(formatLetterNumber(letterId));
      }
    } catch {
      message.error('Failed to load letter');
    } finally {
      setLoadingLetter(false);
    }
  };

  const handleCustomerTypeChange = (type: 'personal' | 'business') => {
    setCustomerType(type);
    setSelectedCustomer(null);
    setSelectedBusiness(null);
    form.setFieldsValue({
      type,
      reference_id: undefined,
      customer_id: undefined,
      business_id: undefined,
      team_id: undefined,
    });
  };

  const handleBusinessChange = async (businessId: number) => {
    if (businessId) {
      try {
        const business = await businessService.getById(businessId);
        setSelectedBusiness(business);
        if (business.customer_id) {
          const customer = await customerService.getById(business.customer_id);
          setSelectedCustomer(customer);
          form.setFieldsValue({
            customer_id: business.customer_id,
            reference_id: businessId,
            team_id: customer.team_id || undefined,
          });
        } else {
          form.setFieldsValue({
            customer_id: undefined,
            reference_id: businessId,
            team_id: undefined,
          });
        }
      } catch {
        message.error('Failed to load business details');
      }
    } else {
      setSelectedBusiness(null);
      setSelectedCustomer(null);
      form.setFieldsValue({
        customer_id: undefined,
        reference_id: undefined,
        team_id: undefined,
      });
    }
  };

  const handleCustomerChange = async (customerId: number) => {
    if (customerId) {
      try {
        const customer = await customerService.getById(customerId);
        setSelectedCustomer(customer);
        form.setFieldsValue({ team_id: customer.team_id, reference_id: customerId });
      } catch {
        message.error('Failed to load customer');
      }
    } else {
      setSelectedCustomer(null);
      form.setFieldsValue({ team_id: null, reference_id: undefined });
    }
  };

  const handleSubjectChange = (subjectId: number | null) => {
    if (!subjectId) {
      setLetterSubTasks([]);
      return;
    }
    const subject = subjects.find(s => s.id === subjectId);
    const matchedTemplate = subject
      ? taskTemplates.find(t => t.name === subject.name) || taskTemplates.find(t => t.id === subjectId)
      : taskTemplates.find(t => t.id === subjectId);
    const templateId = matchedTemplate?.id;
    if (!templateId) {
      setLetterSubTasks([]);
      return;
    }
    const matchedTemplates = subTaskTemplates.filter((st) => st.task_template_id === templateId);
    if (matchedTemplates.length === 0) {
      setLetterSubTasks([]);
      return;
    }
    const now = Date.now();
    const mapped = matchedTemplates.map((st, idx) => ({
      id: `temp-subtask-${now}-${idx}`,
      letter_id: id ? parseInt(id, 10) : 0,
      type: 'letters',
      name: st.name || '',
      completed: false,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
    })) as any[];
    setLetterSubTasks(mapped);
  };

  const handleAddSubject = async () => {
    if (!newSubjectName.trim()) {
      message.error('Please enter a subject name');
      return;
    }
    try {
      const newSubject = await subjectService.create({ name: newSubjectName });
      setSubjects(prev => [...prev, newSubject]);
      form.setFieldsValue({ subject_id: newSubject.id });
      handleSubjectChange(newSubject.id);
      setNewSubjectName('');
      setSubjectDrawerVisible(false);
      message.success('Subject created successfully');
    } catch {
      message.error('Failed to create subject');
    }
  };

  const handleAddSubTask = () => {
    const newSubTask: any = {
      id: `temp-subtask-${Date.now()}`,
      letter_id: id ? parseInt(id, 10) : 0,
      type: 'letters',
      name: '',
      completed: false,
    };
    setLetterSubTasks(prev => [...prev, newSubTask]);
  };

  const handleAddComment = () => {
    if (!commentText.trim()) {
      message.error('Please enter a comment');
      return;
    }
    const newComment: any = {
      id: `temp-comment-${Date.now()}`,
      letter_id: id ? parseInt(id, 10) : 0,
      comment: commentText,
      created_at: new Date().toISOString(),
      user: { name: user?.name || 'Current User' },
    };
    setComments(prev => [...prev, newComment]);
    setCommentText('');
    message.success('Comment added (will be saved on submit)');
  };

  const handleDeleteComment = async (commentId: number | string) => {
    if (typeof commentId === 'number') {
      try {
        await letterCommentService.delete(commentId);
        setComments(prev => prev.filter(c => String(c.id) !== String(commentId)));
        message.success('Comment deleted');
        return;
      } catch {
        message.error('Failed to delete comment');
        if (id) {
          loadLetter(parseInt(id, 10));
        }
        return;
      }
    }
    setComments(prev => prev.filter(c => String(c.id) !== String(commentId)));
  };

  const handleAddReview = () => {
    if (!reviewText.trim()) {
      message.error('Please enter a review');
      return;
    }
    const newReview: any = {
      id: `temp-review-${Date.now()}`,
      letter_id: id ? parseInt(id, 10) : 0,
      customer_id: form.getFieldValue('customer_id'),
      review: reviewText,
      created_at: new Date().toISOString(),
      creator: { name: user?.name || 'Current User' },
    };
    setReviews(prev => [...prev, newReview]);
    setReviewText('');
    message.success('Review added (will be saved on submit)');
  };

  const handleAddLetterItem = (file: File) => {
    // Add to local state only, don't upload to backend yet
    const tempId = `temp-letter-${Date.now()}-${Math.random()}`;
    const newItem: any = {
      id: tempId,
      letter_id: id ? parseInt(id, 10) : 0,
      attachment: null,
      enabled: true,
      created_at: new Date().toISOString(),
      creator: user ? { id: user.id, name: user.name, email: user.email || '' } : undefined,
      file: file, // Store the file object for later upload
    };
    
    // Add to local state
    setLetterItems(prev => [newItem, ...prev]);
    
    // Add to upload queue
    setUploadQueue(prev => [...prev, { file, id: tempId }]);
    setUploadStatus(prev => ({ ...prev, [tempId]: 'pending' }));
    setUploadProgress(prev => ({ ...prev, [tempId]: 0 }));
    
    message.success('Letter item added (will be saved on submit)');
  };

  const handleDeleteLetterItem = (itemId: number | string) => {
    // If it's a temp ID (string), just remove from local state
    if (typeof itemId === 'string' && itemId.startsWith('temp-letter-')) {
      setLetterItems(prev => prev.filter(item => {
        const iId = item.id;
        if (typeof iId === 'string') {
          return iId !== itemId;
      }
        return true; // Keep non-string IDs
      }));
      // Remove from upload queue
      setUploadQueue(prev => prev.filter(item => item.id !== itemId));
      setUploadStatus(prev => {
        const newStatus = { ...prev };
        delete newStatus[itemId];
        return newStatus;
      });
      setUploadProgress(prev => {
        const newProgress = { ...prev };
        delete newProgress[itemId];
        return newProgress;
      });
      message.success('Letter item removed');
      return;
    }
    
    // If it's an existing item (number), delete immediately
    if (typeof itemId === 'number') {
      setLetterItems(prev => prev.filter(item => {
        const iId = item.id;
        if (typeof iId === 'number') {
          return iId !== itemId;
        }
        return true; // Keep non-number IDs
      }));
      // Delete from backend immediately
      letterItemService.delete(itemId).then(() => {
      message.success('Letter item deleted successfully');
      }).catch(() => {
      message.error('Failed to delete letter item');
        // Reload the items if deletion failed
        if (id) {
          letterService.getById(parseInt(id, 10)).then(letter => {
            setLetterItems(letter.items || []);
          });
        }
      });
    }
  };

  const handleViewLetterItem = async (itemId: number) => {
    try {
      const blob = await letterItemService.view(itemId);
      const url = window.URL.createObjectURL(blob);
      window.open(url, '_blank');
      // Clean up the URL after a delay
      setTimeout(() => window.URL.revokeObjectURL(url), 100);
    } catch {
      message.error('Failed to view attachment');
    }
  };

  const handleDownloadLetterItem = async (itemId: number | string) => {
    // If it's a temp ID, don't try to download
    if (typeof itemId === 'string' && itemId.startsWith('temp-letter-')) {
      return;
    }
    if (typeof itemId !== 'number') return;
    try {
      const blob = await letterItemService.download(itemId);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const item = letterItems.find(i => i.id === itemId);
      const filename = item?.attachment ? item.attachment.split('/').pop() || 'attachment' : 'attachment';
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      message.success('Attachment downloaded successfully');
    } catch {
      message.error('Failed to download attachment');
    }
  };

  const handleSubmit = async (values: any) => {
    setSubmitting(true);
    try {
      const referenceId = customerType === 'business' ? values.business_id : values.customer_id;
      const payload: CreateLetterData = {
        ...values,
        create_date: values.create_date?.format('YYYY-MM-DD'),
        due_date: values.due_date?.format('YYYY-MM-DD'),
        // If business_id is selected, use business's customer_id
        type: customerType,
        reference_id: referenceId,
        customer_id: values.business_id ? selectedBusiness?.customer_id : values.customer_id,
        business_id: values.business_id || undefined,
        // Ensure status is 'Plan' for new letters
        status: values.status || 'Plan',
        is_sent: values.is_sent !== undefined ? values.is_sent : false,
        sub_tasks: letterSubTasks
          .filter(st => st.name && st.name.trim())
          .map(st => ({
            name: st.name,
            completed: st.completed || false,
          })),
      };

      let letterId: number;
      if (isEdit && id) {
        await letterService.update(parseInt(id, 10), payload);
        letterId = parseInt(id, 10);
        message.success('Letter updated successfully');
      } else {
        const newLetter = await letterService.create(payload);
        letterId = newLetter.id;
        message.success('Letter created successfully');
      }

      // Save comments
      const newComments = comments.filter(c => {
        const commentId = c.id as number | string | undefined;
        return commentId !== undefined && typeof commentId === 'string' && String(commentId).startsWith('temp-');
      });
      for (const comment of newComments) {
        await letterCommentService.create({
          letter_id: letterId,
          comment: (comment as any).comment || '',
        });
      }

      // Save reviews
      const newReviews = reviews.filter(r => typeof r.id === 'string' && String(r.id).startsWith('temp-'));
      for (const review of newReviews) {
        await letterReviewService.create({
          letter_id: letterId,
          customer_id: form.getFieldValue('customer_id'),
          review: (review as any).review || '',
        });
      }

      // Save letter items (upload files)
      const newLetterItems = letterItems.filter(item => {
        const itemId = item.id;
        return typeof itemId === 'string' && itemId.startsWith('temp-letter-');
      });
      
      for (const item of newLetterItems) {
        const tempItem = item as any;
        if (tempItem.file) {
          try {
            await letterItemService.create({
              letter_id: letterId,
              attachment: tempItem.file,
            });
          } catch (error) {
            console.error('Failed to upload letter item:', error);
            message.error(`Failed to upload ${tempItem.file.name}`);
          }
        }
      }

      navigate('/letters');
    } catch {
      message.error(isEdit ? 'Failed to update letter' : 'Failed to create letter');
    } finally {
      setSubmitting(false);
    }
  };

  const formatLetterNumber = (letterId: number) => {
    return `#${letterId.toString().padStart(7, '0')}`;
  };

  const getDisplayValue = (fieldName: string, type: 'select' | 'date' | 'number' | 'text' = 'text', options?: any[]) => {
    const value = form.getFieldValue(fieldName);
    if (value === null || value === undefined || value === '') {
      return '-';
    }
    
    if (type === 'select' && options) {
      const option = options.find(opt => opt.id === value);
      return option?.name || '-';
    }
    
    if (type === 'date') {
      if (!value) return '-';
      if (dayjs.isDayjs(value)) {
        return value.format('DD/MM/YYYY');
      }
      if (typeof value === 'string') {
        return dayjs(value).format('DD/MM/YYYY');
      }
      return '-';
    }
    
    if (type === 'number') {
      return value || '-';
    }
    
    return value || '-';
  };

  const getLetterDisplayName = () => {
    if (isEdit && id) {
      const subject = subjects.find(s => s.id === form.getFieldValue('subject_id'));
      const isBusiness = Boolean(form.getFieldValue('business_id'));
      return `${formatLetterNumber(parseInt(id, 10))} - ${isBusiness ? 'Business' : 'Personal'} - ${subject?.name || 'Subject'}`;
    }
    return 'New Letter';
  };

  useEffect(() => {
    if (isEdit && id && customers.length > 0 && subjects.length > 0) {
      const displayName = getLetterDisplayName();
      setLetterName(displayName);
    }
  }, [isEdit, id, customers, subjects, form.getFieldValue('customer_id'), form.getFieldValue('subject_id')]);

  const formatDateTime = (dateString?: string) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear();
    const hours = date.getHours();
    const minutes = date.getMinutes().toString().padStart(2, '0');
    const ampm = hours >= 12 ? 'pm' : 'am';
    const displayHours = hours % 12 || 12;
    return `${day}/${month}/${year} ${displayHours}:${minutes}${ampm}`;
  };

  const handleDelete = async () => {
    if (!id) return;
    if (!window.confirm('Are you sure you want to delete this letter?')) return;
    
    try {
      await letterService.delete(parseInt(id, 10));
                      message.success('Letter deleted');
                      navigate('/letters');
    } catch {
      message.error('Failed to delete letter');
                  }
  };

  return (
    <Card bordered={false} bodyStyle={{ padding: 0 }} style={{ boxShadow: 'none' }}>
      {isEdit && letterName && (
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: 24,
          paddingBottom: 16,
          borderBottom: '1px solid #f0f0f0',
        }}>
          <Title level={2} style={{ margin: 0, color: '#1890ff', fontSize: 28, fontWeight: 600 }}>
            {letterName}
          </Title>
          <Space>
            {!isEditing && canUpdate && (
              <Button onClick={() => setIsEditing(true)}>
                Edit
              </Button>
            )}
            {canDelete && (
              <Button danger icon={<DeleteOutlined />} onClick={handleDelete}>
                Delete
              </Button>
            )}
          </Space>
        </div>
      )}
      
      {!isEdit && (
        <Title level={3} style={{ marginBottom: 24 }}>
          New Letter
        </Title>
      )}

      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        disabled={loadingLetter || (isEdit && !isEditing)}
        onValuesChange={(changedValues) => {
          if (changedValues.customer_id !== undefined || changedValues.business_id !== undefined || changedValues.subject_id !== undefined) {
            const displayName = getLetterDisplayName();
            setLetterName(displayName);
          }
        }}
      >
        <Form.Item name="type" hidden>
          <Input />
        </Form.Item>
        <Form.Item name="reference_id" hidden>
          <Input />
        </Form.Item>
        <Tabs 
          activeKey={activeTab}
          onChange={handleTabChange}
          tabBarExtraContent={
            <Form.Item shouldUpdate noStyle>
              {() => {
                // Map old status values to new ones for display
                const mapStatus = (status: string | undefined): 'Plan' | 'Unread' | 'Read' => {
                  if (!status) return 'Plan';
                  if (status === 'draft') return 'Plan';
                  if (status === 'sent') return 'Unread';
                  if (['Plan', 'Unread', 'Read'].includes(status)) return status as 'Plan' | 'Unread' | 'Read';
                  return 'Plan';
                };
                const rawStatus = form.getFieldValue('status');
                const currentStatus = mapStatus(rawStatus);
                const statuses: Array<'Plan' | 'Unread' | 'Read'> = ['Plan', 'Unread', 'Read'];
                const statusConfig = {
                  Plan: { bg: '#E6F7FF', border: '#40A9FF', color: '#1890ff', dot: '#1890ff', label: 'Plan' },
                  Unread: { bg: '#FFF7E6', border: '#FFA940', color: '#FF8C00', dot: '#FF8C00', label: 'Unread' },
                  Read: { bg: '#F6FFED', border: '#73D13D', color: '#52C41A', dot: '#52C41A', label: 'Read' },
                };
                
                // Debug logging
                console.log('🔍 Status Debug:', {
                  rawStatus,
                  currentStatus,
                  allFormValues: form.getFieldsValue(),
                  isActiveStatus: currentStatus,
                  activeConfig: statusConfig[currentStatus],
                });
                
                return (
                  <Space size={8}>
                    {statuses.map((status) => {
                      const isActive = currentStatus === status;
                      const config = statusConfig[status];
                      console.log(`  Status "${status}":`, {
                        isActive,
                        config,
                      });
                      return (
                        <div
                          key={status}
                          style={{
                            padding: '4px 12px',
                            borderRadius: '12px',
                            backgroundColor: isActive ? config.bg : '#f5f5f5',
                            border: isActive ? `1px solid ${config.border}` : '1px solid #d9d9d9',
                            color: isActive ? config.color : '#595959',
                            fontWeight: isActive ? 500 : 400,
                            fontSize: '12px',
                            display: 'flex',
                            alignItems: 'center',
                            gap: 4,
                            transition: 'all 0.2s',
                          }}
                        >
                          <span style={{
                            width: 4,
                            height: 4,
                            borderRadius: '50%',
                            backgroundColor: isActive ? config.dot : '#8c8c8c',
                          }}></span>
                          {config.label}
                        </div>
                      );
                    })}
                  </Space>
                );
              }}
            </Form.Item>
          }
        >
          {/* Overview Tab */}
          <TabPane tab="Overview" key="overview">
            <Card style={{ marginBottom: 8 }} bodyStyle={{ padding: 0 }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <tbody>
                  {/* Customer Type - 单独一行，合并后三列 */}
                  <tr style={{ borderBottom: '1px solid #f0f0f0' }}>
                    <td style={{ padding: '12px', width: '120px', verticalAlign: 'top', backgroundColor: '#fafafa' }}>
                      <strong>Customer</strong>
                    </td>
                    <td colSpan={3} style={{ padding: '12px', verticalAlign: 'top' }}>
                      {isEditing ? (
                        <Space>
                          <Button
                            type={customerType === 'personal' ? 'primary' : 'default'}
                            onClick={() => handleCustomerTypeChange('personal')}
                            size="small"
                          >
                            Personal
                          </Button>
                          <Button
                            type={customerType === 'business' ? 'primary' : 'default'}
                            onClick={() => handleCustomerTypeChange('business')}
                            size="small"
                          >
                            Business
                          </Button>
                        </Space>
                      ) : (
                        <div>
                          {customerType === 'personal' ? 'Personal' : 'Business'}
                      </div>
                      )}
                    </td>
                  </tr>
                  {/* Customer/Business - 单独一行，合并后三列 */}
                  <tr style={{ borderBottom: '1px solid #f0f0f0' }}>
                    <td style={{ padding: '12px', width: '120px', verticalAlign: 'top', backgroundColor: '#fafafa' }}>
                      <strong>{customerType === 'personal' ? 'Customer' : 'Business'}</strong>
                    </td>
                    <td colSpan={3} style={{ padding: '12px', verticalAlign: 'top' }}>
                      {isEditing ? (
                        customerType === 'personal' ? (
                          <Form.Item
                            name="customer_id"
                            rules={[{ required: true, message: 'Please select customer' }]}
                            style={{ margin: 0 }}
                          >
                            <Select
                              placeholder="Select Customer"
                              allowClear
                              showSearch
                              optionFilterProp="children"
                              onChange={handleCustomerChange}
                            >
                              {customers.map((customer) => (
                                <Select.Option key={customer.id} value={customer.id}>
                                  {customer.name}
                                </Select.Option>
                              ))}
                            </Select>
                          </Form.Item>
                        ) : (
                          <Form.Item
                            name="business_id"
                            rules={[{ required: true, message: 'Please select business' }]}
                            style={{ margin: 0 }}
                          >
                            <Select
                              placeholder="Select Business"
                              allowClear
                              showSearch
                              optionFilterProp="children"
                              onChange={handleBusinessChange}
                            >
                              {businesses.map((business) => (
                                <Select.Option key={business.id} value={business.id}>
                                  {business.name}
                                </Select.Option>
                              ))}
                            </Select>
                          </Form.Item>
                        )
                      ) : (
                        <div>
                          {customerType === 'personal' 
                            ? getDisplayValue('customer_id', 'select', customers)
                            : getDisplayValue('business_id', 'select', businesses)
                          }
                      </div>
                      )}
                    </td>
                  </tr>
                  {/* Sender - 单独一行，合并后三列 */}
                  <tr style={{ borderBottom: '1px solid #f0f0f0' }}>
                    <td style={{ padding: '12px', width: '120px', verticalAlign: 'top', backgroundColor: '#fafafa' }}>
                      <strong>Sender</strong>
                    </td>
                    <td colSpan={3} style={{ padding: '12px', verticalAlign: 'top' }}>
                      <Form.Item name="sender_id" hidden>
                        <Input />
                      </Form.Item>
                      <Form.Item shouldUpdate noStyle>
                        {() => {
                          const senderId = form.getFieldValue('sender_id');
                          const sender = staff.find(s => s.id === senderId);
                          const senderName = sender?.name || (isEditing ? user?.name : '') || '';
                          return (
                            <Input
                              value={senderName}
                              placeholder="Sender"
                              disabled
                            />
                          );
                        }}
                      </Form.Item>
                    </td>
                  </tr>
                  {/* Subject - 单独一行，合并后三列 */}
                  <tr style={{ borderBottom: '1px solid #f0f0f0' }}>
                    <td style={{ padding: '12px', width: '120px', verticalAlign: 'top', backgroundColor: '#fafafa' }}>
                      <strong>Subject</strong>
                    </td>
                    <td colSpan={3} style={{ padding: '12px', verticalAlign: 'top' }}>
                      {isEditing ? (
                        <Form.Item name="subject_id" style={{ margin: 0 }}>
                          <Select
                            placeholder="Select Subject"
                            allowClear
                            onChange={(value) => handleSubjectChange(value || null)}
                            dropdownRender={(menu) => (
                              <>
                                {menu}
                                <div style={{ padding: '8px', borderTop: '1px solid #f0f0f0' }}>
                                  <Button
                                    type="link"
                                    icon={<PlusOutlined />}
                                    onClick={() => setSubjectDrawerVisible(true)}
                                    style={{ width: '100%' }}
                                  >
                                    Add Subject
                                  </Button>
                                </div>
                              </>
                            )}
                          >
                            {subjects.map((subject) => (
                              <Select.Option key={subject.id} value={subject.id}>
                                {subject.name}
                              </Select.Option>
                            ))}
                          </Select>
                        </Form.Item>
                      ) : (
                        <div>
                          {getDisplayValue('subject_id', 'select', subjects)}
                      </div>
                      )}
                    </td>
                  </tr>
                  {/* Create 和 Due - 同一行，各占两列 */}
                  <tr style={{ borderBottom: '1px solid #f0f0f0' }}>
                    <td style={{ padding: '12px', width: '120px', verticalAlign: 'top', backgroundColor: '#fafafa' }}>
                      <strong>Create</strong>
                    </td>
                    <td style={{ padding: '12px', width: 'calc((100% - 240px) / 2)', verticalAlign: 'top' }}>
                      {isEditing ? (
                        <Form.Item name="create_date" style={{ margin: 0 }}>
                          <DatePicker
                            style={{ width: '100%' }}
                            format="DD/MM/YYYY"
                            placeholder="Enter Create Date"
                            allowClear
                            onChange={(date) => {
                              form.setFieldsValue({ create_date: date || null });
                            }}
                          />
                        </Form.Item>
                      ) : (
                        <div>
                          {getDisplayValue('create_date', 'date')}
                      </div>
                      )}
                    </td>
                    <td style={{ padding: '12px', width: '120px', verticalAlign: 'top', backgroundColor: '#fafafa' }}>
                      <strong>Due</strong>
                    </td>
                    <td style={{ padding: '12px', width: 'calc((100% - 240px) / 2)', verticalAlign: 'top' }}>
                      {isEditing ? (
                        <Form.Item name="due_date" style={{ margin: 0 }}>
                          <DatePicker
                            style={{ width: '100%' }}
                            format="DD/MM/YYYY"
                            placeholder="Enter Due Date"
                            allowClear
                            onChange={(date) => {
                              form.setFieldsValue({ due_date: date || null });
                            }}
                          />
                        </Form.Item>
                      ) : (
                        <div>
                          {getDisplayValue('due_date', 'date')}
                      </div>
                      )}
                    </td>
                  </tr>
                  {/* Team Advisor - 单独一行，合并后三列 */}
                  <tr style={{ borderBottom: '1px solid #f0f0f0' }}>
                    <td style={{ padding: '12px', width: '120px', verticalAlign: 'top', backgroundColor: '#fafafa' }}>
                      <strong>Team Advisor</strong>
                    </td>
                    <td colSpan={3} style={{ padding: '12px', verticalAlign: 'top' }}>
                      <Input
                        value={selectedCustomer?.agent?.name || ''}
                        placeholder="Direct link after select customer"
                        disabled
                      />
                    </td>
                  </tr>
                  {/* Team - 单独一行，合并后三列 */}
                  <tr style={{ borderBottom: '1px solid #f0f0f0' }}>
                    <td style={{ padding: '12px', width: '120px', verticalAlign: 'top', backgroundColor: '#fafafa' }}>
                      <strong>Team</strong>
                    </td>
                    <td colSpan={3} style={{ padding: '12px', verticalAlign: 'top' }}>
                      {isEditing ? (
                        <Form.Item name="team_id" style={{ margin: 0 }}>
                          <Form.Item shouldUpdate={(prevValues, currentValues) => prevValues.customer_id !== currentValues.customer_id} noStyle>
                            {({ getFieldValue }) => {
                              const teamId = getFieldValue('team_id');
                              const team = teams.find(t => t.id === teamId);
                              return (
                                <Input 
                                  placeholder="Direct link after select customer" 
                                  disabled 
                                  value={team?.name || ''}
                                />
                              );
                            }}
                          </Form.Item>
                        </Form.Item>
                      ) : (
                        <div>
                          <Form.Item shouldUpdate={(prevValues, currentValues) => prevValues.customer_id !== currentValues.customer_id} noStyle>
                            {({ getFieldValue }) => {
                              const teamId = getFieldValue('team_id');
                              const team = teams.find(t => t.id === teamId);
                              return team?.name || '-';
                            }}
                        </Form.Item>
                      </div>
                      )}
                    </td>
                  </tr>
                </tbody>
              </table>
            </Card>
          </TabPane>

          {/* Checklist Tab */}
          <TabPane tab="Checklist" key="checklist">
            <Card
              title="Sub-Tasks"
              bodyStyle={{ padding: '16px' }}
              extra={
                              <Button
                                type="link"
                            icon={<PlusOutlined />}
                  onClick={handleAddSubTask}
                          >
                  Add
                          </Button>
              }
            >
              {letterSubTasks.map((subTask, index) => (
                <div key={subTask.id || index} style={{ padding: '12px 0', borderBottom: '1px solid #f0f0f0' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                    <Checkbox
                      checked={subTask.completed}
                      onChange={(e) => {
                        setLetterSubTasks(prev =>
                          prev.map(st =>
                            st.id === subTask.id
                              ? { ...st, completed: e.target.checked }
                              : st
                          )
                        );
                      }}
                    />
                    <Input
                      value={subTask.name || ''}
                      onChange={(e) => {
                        setLetterSubTasks(prev =>
                          prev.map(st =>
                            st.id === subTask.id
                              ? { ...st, name: e.target.value }
                              : st
                          )
                        );
                      }}
                      style={{ flex: 1 }}
                      placeholder="Enter sub task name"
                    />
                              <Button
                                type="link"
                                danger
                                icon={<DeleteOutlined />}
                                onClick={() => {
                        setLetterSubTasks(prev => prev.filter(st => st.id !== subTask.id));
                                }}
                      size="small"
                              />
                  </div>
                            </div>
                          ))}
              {letterSubTasks.length === 0 && (
                            <div style={{ color: '#999', textAlign: 'center', padding: '20px 0' }}>
                  No sub tasks. Click 'Add' to add a sub task.
                            </div>
                          )}
            </Card>
          </TabPane>

          {/* Letter Tab */}
          <TabPane tab="Letter" key="letter">
            <Card
              title="Letters"
              bodyStyle={{ padding: '16px' }}
              extra={
                isEdit ? (
                  <Upload
                    beforeUpload={(file) => {
                      handleAddLetterItem(file);
                      return false;
                    }}
                    showUploadList={false}
                  >
                    <Button type="link" icon={<PlusOutlined />}>Add</Button>
                  </Upload>
                ) : (
                  <span style={{ color: '#999' }}>Save letter first</span>
                )
              }
            >
              {/* Upload Progress */}
              {uploadQueue.length > 0 && (
                <div style={{ marginBottom: 16, padding: '12px', background: '#fafafa', borderRadius: 4 }}>
                  <div style={{ marginBottom: 8 }}>
                    <Text strong>Uploading {uploadQueue.length} file(s)</Text>
                  </div>
                  <div style={{ maxHeight: 200, overflowY: 'auto' }}>
                    {uploadQueue.map((item) => {
                      const status = uploadStatus[item.id] || 'pending';
                      const progress = uploadProgress[item.id] || 0;
                      return (
                        <div key={item.id} style={{ marginBottom: 8 }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                            <Text style={{ fontSize: '12px' }} ellipsis>
                              {item.file.name}
                            </Text>
                            <Tag
                              color={
                                status === 'success' ? 'green' :
                                status === 'error' ? 'red' :
                                status === 'uploading' ? 'blue' : 'default'
                              }
                            >
                              {status === 'success' ? 'Done' :
                               status === 'error' ? 'Failed' :
                               status === 'uploading' ? 'Uploading' : 'Pending'}
                            </Tag>
                          </div>
                          {status === 'uploading' && (
                            <Progress percent={progress} size="small" />
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
              {letterItems.map((item) => {
                const itemId: number | string = item.id;
                const isTemp = typeof itemId === 'string' && itemId.startsWith('temp-letter-');
                const fileName = isTemp 
                  ? ('file' in item ? (item as any).file?.name : 'New file')
                  : (item.attachment ? item.attachment.split('/').pop() : 'No attachment');
                
                return (
                  <div key={item.id} style={{ padding: '12px 0', borderBottom: '1px solid #f0f0f0' }}>
                    <div style={{ marginBottom: 4 }}>
                      {isTemp ? (
                        <Space>
                          <Text>{fileName}</Text>
                          <Tag color="orange">Pending</Tag>
                          <Button
                            type="link"
                            danger
                            icon={<DeleteOutlined />}
                            onClick={() => handleDeleteLetterItem(item.id!)}
                            size="small"
                          />
                        </Space>
                      ) : item.attachment ? (
                        <Space>
                          <Button
                            type="link"
                            icon={<EyeOutlined />}
                            onClick={() => handleViewLetterItem(item.id!)}
                            size="small"
                          >
                              {fileName}
                          </Button>
                            <Button
                              type="link"
                              icon={<DownloadOutlined />}
                            onClick={() => handleDownloadLetterItem(item.id!)}
                              size="small"
                            />
                            {isEdit && (
                              <Button
                                type="link"
                                danger
                                icon={<DeleteOutlined />}
                              onClick={() => handleDeleteLetterItem(item.id!)}
                                size="small"
                              />
                            )}
                          </Space>
                      ) : (
                        <Text strong>No attachment</Text>
                      )}
                    </div>
                    <div style={{ fontSize: '12px', color: '#999' }}>
                      By {item.creator?.name || 'Unknown'} {formatDateTime(item.created_at)}
                    </div>
                  </div>
                );
              })}
              {letterItems.length === 0 && (
                <div style={{ color: '#999', textAlign: 'center', padding: '20px 0' }}>
                  No letter items
                </div>
              )}
            </Card>
          </TabPane>

          {/* Comment Tab */}
          <TabPane tab="Comment" key="comment">
            <Card
              title="Comments"
              bodyStyle={{ padding: '16px' }}
            >
              <div style={{ marginBottom: 16 }}>
                {comments.map((comment, index) => (
                  <div
                    key={comment.id || index}
                    style={{
                      padding: '12px 0',
                      borderBottom: '1px solid #f0f0f0',
                    }}
                  >
                    <div style={{ marginBottom: 8, whiteSpace: 'pre-wrap' }}>
                      <Text>{comment.comment || '-'}</Text>
                    </div>
                    <div style={{ fontSize: '12px', color: '#999' }}>
                      By {comment.user?.name || 'Unknown'} • {formatDateTime(comment.created_at)}
                      {isEdit && (
                <Button
                  type="link"
                          danger
                          size="small"
                          icon={<DeleteOutlined />}
                          onClick={() => handleDeleteComment(comment.id)}
                          style={{ marginLeft: 8, padding: 0 }}
                        />
                      )}
                    </div>
                  </div>
                ))}
                {comments.length === 0 && (
                  <div style={{ color: '#999', textAlign: 'center', padding: '20px 0' }}>
                    No comments yet.
                  </div>
                )}
              </div>
              {isEdit && (
                <div style={{ marginTop: 16, paddingTop: 16, borderTop: '1px solid #f0f0f0' }}>
                  <TextArea
                    rows={6}
                    placeholder="Enter comment..."
                    value={commentText}
                    onChange={(e) => setCommentText(e.target.value)}
                  />
                  <Button
                    type="primary"
                    onClick={handleAddComment}
                    style={{ marginTop: 12, width: '100%' }}
                    disabled={!commentText.trim()}
                >
                    Add Comment
                </Button>
                </div>
              )}
            </Card>
          </TabPane>

          {/* Review Tab */}
          <TabPane tab="Review" key="review">
            <Card
              title="Review"
              style={{ marginBottom: 16 }}
            >
              <div style={{ marginBottom: 16 }}>
                {reviews.map((review, index) => (
                  <div
                    key={review.id || index}
                    style={{
                      marginBottom: 8,
                      padding: '8px 12px',
                      background: '#fafafa',
                      borderRadius: 4,
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                    }}
                  >
                    <div style={{ flex: 1 }}>
                      {new Date(review.created_at).toLocaleDateString()} {review.review || '-'} by {review.creator?.name || '-'}
                    </div>
                  <Button
                    type="link"
                    danger
                    icon={<DeleteOutlined />}
                    onClick={() => {
                        setReviews(prev => prev.filter(r => r.id !== review.id));
                    }}
                      style={{ marginLeft: 8 }}
                  />
                </div>
              ))}
                {reviews.length === 0 && (
                <div style={{ color: '#999', textAlign: 'center', padding: '20px 0' }}>
                    No reviews yet.
                </div>
              )}
              </div>
              <div style={{ marginTop: 16 }}>
                <TextArea
                  rows={3}
                  placeholder="Enter review"
                  value={reviewText}
                  onChange={(e) => setReviewText(e.target.value)}
                />
                <Button
                  type="primary"
                  icon={<PlusOutlined />}
                  onClick={handleAddReview}
                  style={{ marginTop: 8, width: '100%' }}
                >
                  Add Review
                </Button>
              </div>
            </Card>
          </TabPane>
        </Tabs>

        <div style={{
          padding: '16px 24px',
          borderTop: '1px solid #f0f0f0',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginTop: 24,
          position: 'sticky',
          bottom: 0,
          backgroundColor: '#fff',
          zIndex: 10,
          
        }}>
          <Form.Item shouldUpdate={(prevValues, currentValues) => prevValues.is_sent !== currentValues.is_sent || prevValues.status !== currentValues.status} noStyle>
            {({ getFieldValue }) => {
              const isSent = getFieldValue('is_sent') || false;
              const currentStatus = getFieldValue('status');
              // Disable checkbox if status is Read, or if not editing
              const isDisabled = (!isEditing && isEdit) || currentStatus === 'Read';
              
              return (
                <Form.Item name="is_sent" valuePropName="checked" noStyle>
              <Checkbox 
                onChange={(e) => {
                      const checked = e.target.checked;
                      // Form.Item will automatically update is_sent
                      // We just need to update status
                      if (checked) {
                        form.setFieldsValue({ status: 'Unread' });
                      } else {
                        // If unchecking and current status is Unread, revert to Plan
                        if (currentStatus === 'Unread') {
                          form.setFieldsValue({ status: 'Plan' });
                        }
                      }
                }}
                    disabled={isDisabled}
              >
                Sent
              </Checkbox>
                </Form.Item>
              );
            }}
          </Form.Item>
          <Space>
          {isEditing && (
            <>
              <Button 
                onClick={() => {
                if (isEdit && id) {
                  setIsEditing(false);
                  // Reload letter data to reset form
                  loadLetter(parseInt(id, 10));
                } else {
                  // For create mode, navigate back
                  navigate('/letters');
                }
                }}
                disabled={false}
              >
                Cancel
              </Button>
              <Button 
                type="primary" 
                onClick={() => {
                  form.submit();
                }} 
                loading={submitting}
              >
                {isEdit ? 'Save' : 'Create'}
              </Button>
            </>
          )}
          </Space>
        </div>
      </Form>
      
      {/* Buttons outside Form to avoid disabled state */}
      {!isEditing && isEdit && (
        <div style={{
          padding: '16px 24px',
          borderTop: '1px solid #f0f0f0',
          display: 'flex',
          justifyContent: 'flex-end',
          marginTop: 24,
          position: 'sticky',
          bottom: 0,
          backgroundColor: '#fff',
          zIndex: 10,
        }}>
          <Button onClick={() => navigate('/letters')}>Cancel</Button>
        </div>
      )}

      {/* Subject Drawer */}
      <Drawer
        title="Add Subject"
        placement="right"
        onClose={() => {
          setSubjectDrawerVisible(false);
          setNewSubjectName('');
        }}
        open={subjectDrawerVisible}
      >
        <Form layout="vertical">
          <Form.Item label="Subject Name">
            <Input
              value={newSubjectName}
              onChange={(e) => setNewSubjectName(e.target.value)}
              placeholder="Enter subject name"
              onPressEnter={handleAddSubject}
            />
          </Form.Item>
          <Form.Item>
            <Button type="primary" onClick={handleAddSubject} block>
              Add Subject
            </Button>
          </Form.Item>
        </Form>
      </Drawer>
    </Card>
  );
};
